import React, { useState, useEffect } from 'react';
import { UserData, Gender, MaritalStatus } from '../types';

interface DreamFormProps {
  initialDreamText?: string;
  onSubmit: (data: UserData) => void;
  onBack: () => void;
}

const DreamForm: React.FC<DreamFormProps> = ({ initialDreamText = '', onSubmit, onBack }) => {
  const [formData, setFormData] = useState<UserData>({
    gender: Gender.MALE,
    maritalStatus: MaritalStatus.SINGLE,
    age: 25,
    dreamDescription: initialDreamText,
    dreamContent: initialDreamText
  });
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (initialDreamText) {
      setFormData(prev => ({
        ...prev,
        dreamDescription: initialDreamText,
        dreamContent: initialDreamText
      }));
    }
  }, [initialDreamText]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const dreamText = formData.dreamDescription || formData.dreamContent || '';
    if (dreamText.trim().length < 10) {
      setValidationError('يرجى كتابة وصف مفصل للحلم (10 أحرف على الأقل)');
      return;
    }
    setValidationError(null);
    onSubmit({ ...formData, dreamDescription: dreamText, dreamContent: dreamText });
  };

  const genders = [Gender.MALE, Gender.FEMALE];
  const maritalStatuses = [
    MaritalStatus.SINGLE,
    MaritalStatus.MARRIED,
    MaritalStatus.DIVORCED,
    MaritalStatus.WIDOWED
  ];

  return (
    <div className="min-h-screen bg-[#1E293B] text-white p-6 font-sans" dir="rtl">
      <div className="max-w-md mx-auto">
        <button 
          onClick={onBack}
          className="mb-6 flex items-center text-emerald-400 hover:text-emerald-300 transition-colors text-sm font-semibold"
        >
          <span className="ml-2">←</span> العودة للرئيسية
        </button>

        <h1 className="text-2xl font-bold mb-2 text-center bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
          تفاصيل الرؤية
        </h1>
        <p className="text-center text-slate-300 text-xs mb-6">
          تساعد البيانات الديموغرافية في الوصول للتأويل الأدق وفق المنهج الإسلامي
        </p>

        {validationError && (
          <div className="mb-4 p-3 bg-rose-500/20 border border-rose-500/40 rounded-xl text-rose-300 text-xs text-center">
            {validationError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700">
            <label className="block text-xs font-semibold mb-3 text-slate-300 uppercase tracking-wider">الجنس</label>
            <div className="grid grid-cols-2 gap-3">
              {genders.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setFormData({ ...formData, gender: g })}
                  className={`py-2.5 px-4 rounded-xl border transition-all text-sm font-medium ${
                    formData.gender === g 
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400 font-bold' 
                      : 'border-slate-700 bg-slate-800/80 hover:border-slate-600 text-slate-300'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700">
            <label className="block text-xs font-semibold mb-3 text-slate-300 uppercase tracking-wider">الحالة الاجتماعية</label>
            <div className="grid grid-cols-2 gap-2.5">
              {maritalStatuses.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setFormData({ ...formData, maritalStatus: s })}
                  className={`py-2 px-3 text-xs rounded-xl border transition-all font-medium ${
                    formData.maritalStatus === s 
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400 font-bold' 
                      : 'border-slate-700 bg-slate-800/80 hover:border-slate-600 text-slate-300'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700">
            <div className="flex justify-between items-center mb-2">
              <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider">العمر</label>
              <span className="text-emerald-400 font-bold text-sm">{formData.age} سنة</span>
            </div>
            <input 
              type="range" 
              min="5" 
              max="100" 
              value={formData.age}
              onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) })}
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
          </div>

          <div className="bg-slate-800/50 p-5 rounded-2xl border border-slate-700">
            <label className="block text-xs font-semibold mb-2 text-slate-300 uppercase tracking-wider">وصف الحلم والرموز</label>
            <textarea
              required
              rows={5}
              placeholder="اكتب حلمك بالتفصيل (مثل: رأيت في منامي أنني أمشي في مكان واسع وظهر ثعبان أخضر...)"
              value={formData.dreamDescription}
              onChange={(e) => setFormData({ ...formData, dreamDescription: e.target.value, dreamContent: e.target.value })}
              className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3.5 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all resize-none text-white placeholder-slate-400 text-sm leading-relaxed"
            />
          </div>

          <button
            type="submit"
            className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold rounded-2xl shadow-xl shadow-emerald-900/30 transition-all active:scale-95 text-base"
          >
            تفسير الحلم بالذكاء الاصطناعي الآن ←
          </button>
        </form>
      </div>
    </div>
  );
};

export default DreamForm;
